<script setup lang="tsx">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { getMethodStatusText, getTypeIcon, getTypeText } from '@/services/utils'
import type {
	MethodImportMatchedMethod as MatchedMethod,
	MethodImportNameMismatch as NameMismatch,
} from '@/models/api-generated'
import {
	statusIcon, importResponsibleIcon, fmtDate, dateDay,
	effectiveStatus as _effectiveStatus, isDoNotImport as _isDoNotImport,
	hasStatusMismatch, hasDiff,
} from './importUtils'

const props = defineProps<{
	grp: { key: string; module: string; moduleTitle: string; assetName: string }
	methods: MatchedMethod[]
	importableMethods: MatchedMethod[]
	selectedIds: Set<number>
	isExpanded: boolean
	mismatchByName: Record<string, NameMismatch>
	statusReplacementMap: Record<string, string>
	approvedVersionMismatch: boolean
}>()

const emit = defineEmits<{ toggle: [] }>()

const { t } = useI18n()

const effectiveStatus = (s: string) => _effectiveStatus(s, props.statusReplacementMap)
const isDoNotImport = (s: string) => _isDoNotImport(s, props.statusReplacementMap)

const isGroupChecked = computed(() =>
	props.importableMethods.length > 0 && props.importableMethods.every((m) => props.selectedIds.has(m.metaId)),
)
const selectedInGroup = computed(() => props.methods.filter((m) => props.selectedIds.has(m.metaId)).length)
const diffCount = computed(() => props.methods.filter(hasDiff).length)
const secWarnSelected = computed(() => props.methods.some((m) => m.highSecNeedWarning && props.selectedIds.has(m.metaId)))
const versionMismatchSelected = computed(() =>
	!props.approvedVersionMismatch && props.methods.some((m) => props.mismatchByName[m.name] && props.selectedIds.has(m.metaId)),
)
const statusMismatchSelected = computed(() =>
	props.methods.some((m) => hasStatusMismatch(m.imported.status) && !props.statusReplacementMap[m.imported.status] && props.selectedIds.has(m.metaId)),
)
const responsibleIssueCount = computed(() =>
	props.methods.filter((m) => m.imported.responsibleLabel && !m.imported.responsibleId && props.selectedIds.has(m.metaId)).length,
)

function toggleGroup(e: Event) {
	const checked = (e.target as HTMLInputElement).checked
	for (const m of props.importableMethods) {
		if (checked) props.selectedIds.add(m.metaId)
		else props.selectedIds.delete(m.metaId)
	}
}

function toggleMethod(e: Event, id: number) {
	if ((e.target as HTMLInputElement).checked) props.selectedIds.add(id)
	else props.selectedIds.delete(id)
}
</script>

<template>
	<div class="card mb-2">
		<div
			class="card-header d-flex flex-wrap align-items-center gap-2 py-2 cursor-pointer"
			@click="emit('toggle')"
		>
			<input
				type="checkbox"
				class="form-check-input flex-shrink-0"
				:checked="isGroupChecked"
				@click.stop
				@change="toggleGroup"
			/>
			<div class="d-flex align-items-center gap-1">
				<i class="bi bi-layers text-muted"></i>
				<span class="fw-semibold">{{ grp.module }}</span>
				<span v-if="grp.moduleTitle" class="text-muted small">— {{ grp.moduleTitle }}</span>
			</div>
			<span v-if="grp.assetName" class="text-muted small">
				<i class="bi bi-clipboard me-1"></i>{{ grp.assetName }}
			</span>
			<span class="badge bg-primary-subtle border border-primary-subtle text-primary fw-normal">
				{{ selectedInGroup }}/{{ methods.length }}
			</span>
			<div class="d-flex align-items-center gap-2 ms-auto">
				<span v-if="diffCount > 0" class="badge bg-info-subtle border border-info-subtle text-info fw-normal">
					<i class="bi bi-pencil-fill me-1 small"></i>{{ diffCount }}
				</span>
				<span v-if="secWarnSelected" class="badge bg-warning text-dark">
					<i class="bi bi-exclamation-triangle me-1"></i>{{ t('method.securityType') }}
				</span>
				<span v-if="versionMismatchSelected" class="badge bg-warning text-dark">
					<i class="bi bi-file-earmark-diff me-1"></i>{{ t('import.versionMismatch') }}
				</span>
				<span v-if="statusMismatchSelected" class="badge bg-danger">
					<i class="bi bi-arrow-repeat me-1"></i>{{ t('import.statusMismatch') }}
				</span>
				<span v-if="responsibleIssueCount > 0" class="badge bg-danger">
					<i class="bi bi-person-x me-1"></i>{{ responsibleIssueCount }}
				</span>
				<i :class="isExpanded ? 'bi bi-chevron-up' : 'bi bi-chevron-down'" class="ms-1 text-muted"></i>
			</div>
		</div>
		<template v-if="isExpanded">
			<div class="table-responsive">
				<table class="table table-sm table-hover align-middle mb-0 matched-table">
					<thead class="table-light">
						<tr>
							<th style="width: 1px"></th>
							<th>{{ t('method.reference') }}</th>
							<th style="width: 1px">{{ t('method.securityType') }}</th>
							<th>{{ t('method.status', 1) }}</th>
							<th>{{ t('responsible') }}</th>
							<th>{{ t('date.dueDate') }}</th>
							<th class="col-2">{{ t('misc.comment') }}</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="m in methods" :key="m.metaId" :class="{ 'table-warning': hasDiff(m), 'opacity-50': !hasDiff(m) }">
							<td>
								<input
									type="checkbox"
									class="form-check-input"
									:checked="selectedIds.has(m.metaId)"
									:disabled="!hasDiff(m) || isDoNotImport(m.imported.status)"
									@change="(e) => toggleMethod(e, m.metaId)"
								/>
							</td>
							<td class="fw-semibold">
								{{ m.name }}
								<span v-if="!approvedVersionMismatch && mismatchByName[m.name]" class="badge bg-warning text-dark ms-1 fw-normal small" :title="t('import.versionMismatch')">
									<i class="bi bi-file-earmark-diff"></i>
								</span>
								<span v-if="hasStatusMismatch(m.imported.status) && !statusReplacementMap[m.imported.status]" class="badge bg-danger ms-1 fw-normal small" :title="t('import.statusMismatch') + ': ' + m.imported.status">
									<i class="bi bi-arrow-repeat"></i>
								</span>
								<span v-if="m.highSecNeedWarning" class="badge bg-warning text-dark ms-1 fw-normal small" :title="t('method.securityType')">
									<i class="bi bi-exclamation-triangle"></i>
								</span>
								<span v-if="m.imported.responsibleLabel && !m.imported.responsibleId" class="badge bg-danger ms-1 fw-normal small" :title="t('import.responsibleNotFound') + ': ' + m.imported.responsibleLabel">
									<i class="bi bi-person-x"></i>
								</span>
								<div v-if="m.methodTitle" class="text-muted small fw-normal">{{ m.methodTitle }}</div>
								<div v-if="!hasDiff(m)" class="small fst-italic text-muted mt-1">
									<i class="bi bi-info-circle me-1"></i>{{ t('import.noChangesDetected') }}
								</div>
								<div v-if="!approvedVersionMismatch && mismatchByName[m.name]" class="version-mismatch-detail">
									{{ mismatchByName[m.name].inFile }} → {{ mismatchByName[m.name].inDB }}
								</div>
							</td>
							<td>
								<i :class="getTypeIcon(m.methodType)" :title="getTypeText(m.methodType)"></i>
							</td>
							<td class="small">
								<template v-if="m.current.status !== m.imported.status">
									<div class="text-muted">
										<i :class="statusIcon(m.current.status)" class="me-1"></i>{{ getMethodStatusText(m.current.status) || m.current.status || '—' }}
									</div>
									<div class="text-primary fw-semibold">
										<i :class="statusIcon(effectiveStatus(m.imported.status))" class="me-1"></i>{{ getMethodStatusText(effectiveStatus(m.imported.status)) || effectiveStatus(m.imported.status) || '—' }}
									</div>
								</template>
								<div v-else class="text-muted">
									<i :class="statusIcon(m.current.status)" class="me-1"></i>{{ getMethodStatusText(m.current.status) || '—' }}
								</div>
							</td>
							<td class="small">
								<span v-if="m.imported.responsibleLabel && !m.imported.responsibleId" class="text-danger">
									<i class="bi bi-person-x me-1"></i>{{ m.imported.responsibleLabel }}
								</span>
								<template v-else-if="m.imported.responsibleId !== null && m.current.responsibleId !== m.imported.responsibleId">
									<div class="text-muted">
										<i :class="'bi ' + importResponsibleIcon(m.current.responsibleType)" class="me-1"></i>{{ m.current.responsibleLabel || '—' }}
									</div>
									<div class="text-primary fw-semibold">
										<i :class="'bi ' + importResponsibleIcon(m.imported.responsibleType)" class="me-1"></i>{{ m.imported.responsibleLabel || '—' }}
									</div>
								</template>
								<div v-else class="text-muted">
									<i :class="'bi ' + importResponsibleIcon(m.current.responsibleType)" class="me-1"></i>{{ m.current.responsibleLabel || '—' }}
								</div>
							</td>
							<td class="small">
								<template v-if="m.imported.dueDate !== null && dateDay(m.current.dueDate) !== dateDay(m.imported.dueDate)">
									<div class="text-muted">{{ fmtDate(m.current.dueDate) }}</div>
									<div class="text-primary fw-semibold">{{ fmtDate(m.imported.dueDate) }}</div>
								</template>
								<div v-else class="text-muted">{{ fmtDate(m.current.dueDate) }}</div>
							</td>
							<td class="small">
								<template v-if="m.imported.comment && m.current.comment !== m.imported.comment">
									<div class="text-muted text-truncate">{{ m.current.comment || '—' }}</div>
									<div class="text-primary fw-semibold text-truncate">{{ m.imported.comment }}</div>
								</template>
								<div v-else class="text-muted text-truncate">{{ m.current.comment || '—' }}</div>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</template>
	</div>
</template>

<style lang="sass" scoped>
.matched-table td
	vertical-align: top
</style>
